import React, { useState, useEffect } from "react";

/**
 * Exemple de composant React reproduisant la frise chronologique 
 * façon "simulation de procédure" (divorce, etc.).
 * 
 * - Utilise TailwindCSS + DaisyUI pour le style de base.
 * - Logique de toggles / étapes / calcul de délais reprise 
 *   de la version HTML, adaptée en React.
 */

const ProcedureSimulation = () => {
  // ---- ÉTATS GLOBAUX ----
  const [totalMinMonths, setTotalMinMonths] = useState(0);
  const [totalMaxMonths, setTotalMaxMonths] = useState(0);
  const [completedChoices, setCompletedChoices] = useState(0);

  // Sur combien de "choix" on calcule la progression
  const totalChoices = 4;

    // Historique des délais ajoutés
  const [historyEntries, setHistoryEntries] = useState([]);

  // Étapes déjà complétées (pour mettre à jour la liste de gauche)
  const [completedSteps, setCompletedSteps] = useState([
    // On considère que les 2 premières étapes (résidence & dépôt) 
    // sont "déjà faites" au chargement
    "step-residence",
    "step-depot",
  ]);

  // State booléens pour éviter de re-comptabiliser un délai au 2e clic
  const [addressChosen, setAddressChosen] = useState(false);
  const [defenderResponseChosen, setDefenderResponseChosen] = useState(false);
  const [settlementChosen, setSettlementChosen] = useState(false);
  const [reglementChosen, setReglementChosen] = useState(false);
  const [revenuChosen, setRevenuChosen] = useState(false);

  // On stocke le "nombre de clics" par bouton pour gérer l'affichage "Terminé" après 2 clics
  const [clickCounts, setClickCounts] = useState({});

  // DÉLAIS (en mois) – à adapter selon votre logique métier
  const Delays = {
    reponse: { min: 0.5, max: 1 }, // 15-30 jours
    defaut: { min: 1, max: 1 }, // Divorce par défaut
    protocole: { min: 3, max: 3 },
    revenu: { min: 0.5, max: 1 },
    ententeRapide: { min: 1, max: 1 },
    contestation: { min: 6, max: 6 },
    proces: { min: 4, max: 4 },
    jugement: { min: 1, max: 1 },
  };

  // Calcul de la progression en pourcentage
  const progressPercentage = (completedChoices / totalChoices) * 100;

  // --------------------------------
  // Aide : Fonctions utilitaires
  // --------------------------------

  // Met à jour les compteurs global totalMinMonths / totalMaxMonths
  // puis le "historyEntries"
  const addDelay = (label, min, max) => {
    setTotalMinMonths((prev) => prev + min);
    setTotalMaxMonths((prev) => prev + max);

    const line = `${label} => (+ ${min} à ${max} mois)`;
    setHistoryEntries((prev) => [...prev, line]);
  };

  // Marque une étape comme complétée (pour la liste de gauche)
  const markStepCompleted = (stepId) => {
    setCompletedSteps((prev) => {
      if (!prev.includes(stepId)) {
        return [...prev, stepId];
      }
      return prev;
    });
  };

  // Incrémente le nombre de clic d'un bouton
  // => gère l'affichage "Terminé" après 2e clic
  // => gère la proposition de réinit après 3e clic
  const incrementClickCount = (buttonKey) => {
    setClickCounts((prev) => {
      const newCount = (prev[buttonKey] || 0) + 1;
      return { ...prev, [buttonKey]: newCount };
    });
  };

  // On vérifie si le bouton a été cliqué au moins "n" fois
  const hasReachedClickCount = (buttonKey, n) => {
    return (clickCounts[buttonKey] || 0) >= n;
  };

  // --------------------------------
  // Handlers de choix
  // --------------------------------

  // Choix spécial : "Connaissez-vous l’adresse de l’autre partie ?"
  const handleAddressChoice = (choice) => {
    const buttonKey = choice === "yes" ? "addressYes" : "addressNo";

    // Incrémente le compteur de clic du bouton
    incrementClickCount(buttonKey);

    // Si c'est la 3e fois qu'on clique => proposition de réinit
    if (hasReachedClickCount(buttonKey, 3)) {
      if (window.confirm("Souhaitez-vous réinitialiser la simulation ?")) {
        handleReset();
      }
      return;
    }

    // Si c'est la 2e fois qu'on clique => on affiche "Terminé" (pas de recalcul)
    if (hasReachedClickCount(buttonKey, 2)) {
      return;
    }

    // Sinon, c'est le 1er clic effectif
    if (!addressChosen) {
      setAddressChosen(true);
      setCompletedChoices((prev) => prev + 1);
    }
    // Si adresse inconnue => +1 mois
    if (choice === "no") {
      addDelay("Signification spéciale (adresse inconnue)", 1, 1);
    }
    // Marque l'étape "signification" comme complétée
    markStepCompleted("step-signification");
  };

  // Choix "Le défendeur répond-il ?"
  const handleDefenderResponse = (choice) => {
    const buttonKey =
      choice === "yes" ? "defenderResponseYes" : "defenderResponseNo";

    incrementClickCount(buttonKey);

    // 3e clic => propose reset
    if (hasReachedClickCount(buttonKey, 3)) {
      if (window.confirm("Souhaitez-vous réinitialiser la simulation ?")) {
        handleReset();
      }
      return;
    }
    // 2e clic => Terminé
    if (hasReachedClickCount(buttonKey, 2)) {
      return;
    }

    if (!defenderResponseChosen) {
      setDefenderResponseChosen(true);
      setCompletedChoices((prev) => prev + 1);

      // Ajout du délai 15-30 jours pour la réponse
      addDelay("Réponse du défendeur", Delays.reponse.min, Delays.reponse.max);

      // Marque l'étape "signification" comme complétée
      markStepCompleted("step-signification");
    }

    // S’il répond => on montre protocole
    if (choice === "yes") {
      // Affiche protocole
      // et on ajoute direct 3 mois pour le protocole
      addDelay("Protocole d’instance", Delays.protocole.min, Delays.protocole.max);
      markStepCompleted("step-protocole");
    } else {
      // S’il ne répond pas => divorce par défaut
      addDelay("Divorce par défaut", Delays.defaut.min, Delays.defaut.max);

      // On affiche la case "Revenu estimé" (pension)
      if (!revenuChosen) {
        setRevenuChosen(true);
        addDelay(
          "Revenu estimé & prélèvement RQ",
          Delays.revenu.min,
          Delays.revenu.max
        );
      }

      // On ajoute direct le délai du jugement final
      addDelay("Jugement final (défaut)", Delays.jugement.min, Delays.jugement.max);

      // On masque protocole, entente, contestation...
      // => Dans un vrai composant, on rendrait ces sections conditionnelles
      markStepCompleted("step-default");
      markStepCompleted("step-jugement");
    }
  };

  // Choix "Une entente est-elle envisageable ?"
  const handleSettlementChoice = (choice) => {
    const buttonKey = choice === "yes" ? "settlementYes" : "settlementNo";

    incrementClickCount(buttonKey);

    // 3e clic => propose reset
    if (hasReachedClickCount(buttonKey, 3)) {
      if (window.confirm("Souhaitez-vous réinitialiser la simulation ?")) {
        handleReset();
      }
      return;
    }
    // 2e clic => Terminé
    if (hasReachedClickCount(buttonKey, 2)) {
      return;
    }

    if (!settlementChosen) {
      setSettlementChosen(true);
      setCompletedChoices((prev) => prev + 1);

      // S’il y a entente : homologation rapide
      if (choice === "yes") {
        addDelay(
          "Protocole : Entente & homologation",
          Delays.ententeRapide.min,
          Delays.ententeRapide.max
        );
        markStepCompleted("step-entente");
      } else {
        // S’il n’y a pas entente : contestation => 6 mois
        addDelay(
          "Protocole : Contestation (CRA + Procès)",
          Delays.contestation.min,
          Delays.contestation.max
        );
        markStepCompleted("step-contest");
      }
      markStepCompleted("step-protocole");
    }
  };

  // Choix "Pourra-t-on s’entendre avant le jugement final ?"
  const handleReglementChoice = (choice) => {
    const buttonKey = choice === "yes" ? "reglementYes" : "reglementNo";

    incrementClickCount(buttonKey);

    // 3e clic => propose reset
    if (hasReachedClickCount(buttonKey, 3)) {
      if (window.confirm("Souhaitez-vous réinitialiser la simulation ?")) {
        handleReset();
      }
      return;
    }
    // 2e clic => Terminé
    if (hasReachedClickCount(buttonKey, 2)) {
      return;
    }

    if (!reglementChosen) {
      setReglementChosen(true);
      setCompletedChoices((prev) => prev + 1);

      // De toute façon, procès => +4 mois
      addDelay("Procès / audience", Delays.proces.min, Delays.proces.max);
      markStepCompleted("step-proces");

      if (choice === "yes") {
        // Règlement avant jugement => homologation
        // (pas besoin d'ajouter d'autres mois, 
        //  ou ajouter un +1 si vous voulez un mini-délai)
        markStepCompleted("step-reglement");
      } else {
        // Jugement final => +1 mois (délai d’appel)
        addDelay(
          "Jugement final",
          Delays.jugement.min,
          Delays.jugement.max
        );
        markStepCompleted("step-jugement");
      }
    }
  };

  // --------------------------------
  // Reset / Réinitialiser la simulation
  // --------------------------------
  const handleReset = () => {
    setTotalMinMonths(0);
    setTotalMaxMonths(0);
    setCompletedChoices(0);

    setAddressChosen(false);
    setDefenderResponseChosen(false);
    setSettlementChosen(false);
    setReglementChosen(false);
    setRevenuChosen(false);

    setHistoryEntries([]);
    setClickCounts({});

    // Remettre les étapes initiales seulement
    setCompletedSteps(["step-residence", "step-depot"]);
  };

  // --------------------------------
  // Effet initial : on simule que "résidence" et "dépôt" sont faites
  // --------------------------------
  useEffect(() => {
    // On place la simulation comme si on avait déjà franchi 2 étapes
    // (résidence et dépôt)
    // -> Pas de délai à ajouter pour ces étapes
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-white">
      {/* HEADER */}
      <header className="bg-blue-600 text-white p-4 text-center">
        <h1 className="text-xl font-semibold">
          myPlum+ – Simulation de procédure de divorce (classique)
        </h1>
      </header>

      {/* PROGRESS BAR */}
      <div className="w-full bg-gray-300 h-4 mt-2 rounded-full overflow-hidden max-w-5xl mx-auto">
        <div
          className="bg-blue-600 h-full text-center text-white text-sm font-semibold transition-all duration-500"
          style={{ width: `${progressPercentage}%` }}
        >
          {Math.round(progressPercentage)}%
        </div>
      </div>

      {/* DISCLAIMER */}
      <div className="bg-yellow-50 border-b-2 border-blue-200 text-center p-3 text-sm text-gray-700 my-2">
        <strong>Important :</strong> Les informations ci-dessous sont fournies à titre
        indicatif et ne remplacent pas un avis juridique.
      </div>

      {/* CONTENU PRINCIPAL : 5 colonnes (3 colonnes + 2 barres verticales) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 max-w-7xl mx-auto w-full flex-grow">
        {/* COLONNE GAUCHE */}
        <div className="left-col bg-gray-50 p-4">
          {/* BOUTON RÉINITIALISER */}
          <button
            className="btn btn-error mb-4"
            onClick={handleReset}
          >
            Réinitialiser
          </button>

          {/* Boîte de durée estimée */}
          <div className="bg-yellow-100 border border-yellow-200 rounded p-3 mb-4 text-sm">
            <p>
              <strong>Durée estimée :</strong>{" "}
              {totalMinMonths < 2 && totalMinMonths > 0
                ? 2
                : Math.round(totalMinMonths)}{" "}
              à{" "}
              {totalMaxMonths > 36 ? "36+" : Math.round(totalMaxMonths)} mois
            </p>
            {totalMaxMonths > 36 && (
              <p className="text-orange-700 text-xs">
                (Au-delà de 36 mois, la cour presse souvent le règlement.)
              </p>
            )}
          </div>

          {/* Liste des étapes */}
          <div>
            <h3 className="text-blue-700 font-bold mb-2">Étapes</h3>
            <ul className="space-y-1 text-sm">
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-residence")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-residence"
              >
                1. Condition de résidence
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-depot")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-depot"
              >
                2. Dépôt de la demande
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-signification")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-signification"
              >
                3. Signification de la demande
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-default")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-default"
              >
                4A. Divorce par défaut
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-protocole")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-protocole"
              >
                4B. Protocole d’instance
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-revenu")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-revenu"
              >
                4C. Évaluation du revenu
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-entente")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-entente"
              >
                5. Entente & Homologation
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-contest")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-contest"
              >
                6. Contestation & Préparation
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-cra") ? "bg-blue-500 text-white" : ""
                }`}
                id="left-step-cra"
              >
                7. Conférence de règlement (CRA)
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-proces")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-proces"
              >
                8. Procès
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-reglement")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-reglement"
              >
                9A. Règlement avant jugement
              </li>
              <li
                className={`border px-2 py-1 rounded ${
                  completedSteps.includes("step-jugement")
                    ? "bg-blue-500 text-white"
                    : ""
                }`}
                id="left-step-jugement"
              >
                9B. Jugement final
              </li>
            </ul>
          </div>
        </div>

        {/* BARRE VERTICALE */}
        <div className="hidden lg:block bg-blue-200 w-[5px]" />

        {/* TIMELINE : au milieu */}
        <div className="col-span-1 lg:col-span-2 p-4 bg-white">
          {/* On va afficher ici les "étapes" façon accordéon */}
          <div className="space-y-4 flex flex-col-reverse">
            {/* On inverse l’ordre pour coller à l’exemple qui fait un affichage “inversé”.
                Si vous ne le souhaitez pas, retirez `flex-col-reverse`. */}

            {/* ÉTAPE 9B : Jugement final */}
            <TimelineStep
              stepId="step-jugement"
              title="9B. Jugement final"
              delayLabel="31 jours"
              completed={completedSteps.includes("step-jugement")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Le divorce est officiel 31 jours après le jugement (si pas d’appel).
              </p>
              <p className="text-sm">
                Vous obtiendrez le certificat de divorce passé ce délai. Pensez à mettre
                à jour votre état civil auprès des organismes (ARC, Revenu Québec, etc.).
              </p>
            </TimelineStep>

            {/* ÉTAPE 9A : Règlement avant jugement */}
            <TimelineStep
              stepId="step-reglement"
              title="9A. Règlement avant jugement"
              delayLabel="Homologation"
              completed={completedSteps.includes("step-reglement")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Si les parties s’entendent à l’audience, le juge officialise
                immédiatement l’accord.
              </p>
              <p className="text-sm">
                Vous évitez ainsi la poursuite du procès et raccourcissez la procédure.
              </p>
            </TimelineStep>

            {/* ÉTAPE 8 : Procès */}
            <TimelineStep
              stepId="step-proces"
              title="8. Procès (audience)"
              delayLabel="Variable"
              completed={completedSteps.includes("step-proces")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Les parties présentent leurs témoins, preuves et arguments devant le juge.
              </p>
              <p className="text-sm mb-3">
                Le procès peut durer quelques jours selon la complexité. Le juge peut
                rendre le jugement immédiatement ou prendre du temps pour délibérer.
              </p>
              <div className="bg-gray-100 p-2 rounded">
                <label className="mr-2">Un règlement est-il conclu avant le jugement&nbsp;?</label>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleReglementChoice("yes")}
                >
                  Oui
                </button>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleReglementChoice("no")}
                >
                  Non
                </button>
                {/* Message "Terminé" conditionnel */}
                {hasReachedClickCount("reglementYes", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
                {hasReachedClickCount("reglementNo", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
              </div>
            </TimelineStep>

            {/* ÉTAPE 7 : CRA */}
            <TimelineStep
              stepId="step-cra"
              title="7. Conférence de règlement à l’amiable (CRA)"
              delayLabel="Optionnel"
              completed={completedSteps.includes("step-cra")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Un juge agit comme médiateur pour tenter de conclure un règlement.
              </p>
              <p className="text-sm">
                La CRA est confidentielle. En cas d’accord, le juge l’homologue.
                Sinon, le dossier se poursuit vers le procès.
              </p>
            </TimelineStep>

            {/* ÉTAPE 6 : Contestation & Préparation */}
            <TimelineStep
              stepId="step-contest"
              title="6. Contestation & Préparation"
              delayLabel="~6 mois"
              completed={completedSteps.includes("step-contest")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Les avocats préparent le dossier : preuves, expertises, interrogatoires.
              </p>
              <p className="text-sm">
                Les délais varient selon la complexité. Vous pouvez demander des mesures
                provisoires (pension, garde) si nécessaire.
              </p>
            </TimelineStep>

            {/* ÉTAPE 5 : Entente & Homologation */}
            <TimelineStep
              stepId="step-entente"
              title="5. Entente & Homologation"
              delayLabel="Procédure accélérée"
              completed={completedSteps.includes("step-entente")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Les parties s’entendent sur la garde, la pension et le partage des biens.
              </p>
              <p className="text-sm">
                Le juge valide si l’entente respecte l’intérêt des enfants et les
                dispositions légales. L’homologation est rapide.
              </p>
            </TimelineStep>

            {/* ÉTAPE 4C : Revenu */}
            <TimelineStep
              stepId="step-revenu"
              title="4C. Évaluation du revenu & Prélèvement RQ"
              delayLabel="0.5 à 1 mois"
              completed={completedSteps.includes("step-revenu")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Le tribunal peut fixer un revenu fictif pour la pension et autoriser
                Revenu Québec à prélever directement.
              </p>
            </TimelineStep>

            {/* ÉTAPE 4B : Protocole d’instance */}
            <TimelineStep
              stepId="step-protocole"
              title="4B. Protocole d’instance"
              delayLabel="3 mois"
              completed={completedSteps.includes("step-protocole")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                En cas de réponse du défendeur, un protocole d’instance est exigé.
              </p>
              <p className="text-sm mb-3">
                Vous avez 3 mois pour déposer le protocole après la réponse, en
                organisant la suite du dossier : médiation, échanges de pièces, etc.
              </p>
              <div className="bg-gray-100 p-2 rounded">
                <label className="mr-2">Une entente est-elle envisageable&nbsp;?</label>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleSettlementChoice("yes")}
                >
                  Oui, entente
                </button>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleSettlementChoice("no")}
                >
                  Non, contestation
                </button>
                {hasReachedClickCount("settlementYes", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
                {hasReachedClickCount("settlementNo", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
              </div>
            </TimelineStep>

            {/* ÉTAPE 4A : Divorce par défaut */}
            <TimelineStep
              stepId="step-default"
              title="4A. Divorce par défaut"
              delayLabel="Procédure simplifiée"
              completed={completedSteps.includes("step-default")}
              defaultHidden={true}
            >
              <p className="font-bold mb-2">
                Si le défendeur ne répond pas, vous pouvez obtenir un jugement par défaut.
              </p>
              <p className="text-sm">
                Vous devez prouver la signification correcte et l’écoulement du délai
                de réponse. Le juge analysera vos pièces et pourra prononcer le
                divorce sans l’autre partie.
              </p>
            </TimelineStep>

            {/* ÉTAPE 3 : Signification */}
            <TimelineStep
              stepId="step-signification"
              title="3. Signification de la demande"
              delayLabel="0-3 jours"
              completed={completedSteps.includes("step-signification")}
            >
              <p className="font-bold mb-2">
                La demande doit être remise officiellement à l’autre partie (huissier).
              </p>
              <p className="text-sm">
                Si vous ignorez l’adresse, vous devez demander au tribunal une
                permission de signification spéciale (journal, email, etc.).
              </p>
              <div className="bg-gray-50 p-2 rounded mb-2">
                <label className="mr-2">
                  Connaissez-vous l’adresse de l’autre partie&nbsp;?
                </label>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleAddressChoice("yes")}
                >
                  Oui
                </button>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleAddressChoice("no")}
                >
                  Non
                </button>
                {hasReachedClickCount("addressYes", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
                {hasReachedClickCount("addressNo", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
              </div>
              {addressChosen && !hasReachedClickCount("addressNo", 2) && (
                <p className="text-sm text-gray-600">
                  Après la signification, le défendeur dispose de 15 jours (QC) ou 30
                  jours (hors QC) pour répondre.
                </p>
              )}

              {/* Choix "Le défendeur répond-il ?" */}
              <div className="bg-gray-50 p-2 mt-2 rounded">
                <label className="mr-2">Le défendeur répond-il&nbsp;?</label>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleDefenderResponse("yes")}
                >
                  Oui
                </button>
                <button
                  className="btn btn-sm btn-outline btn-accent mr-2"
                  onClick={() => handleDefenderResponse("no")}
                >
                  Non (défaut)
                </button>
                {hasReachedClickCount("defenderResponseYes", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
                {hasReachedClickCount("defenderResponseNo", 2) && (
                  <span className="text-blue-500 font-semibold ml-2">Terminé</span>
                )}
              </div>
            </TimelineStep>

            /* ÉTAPE 2 : Dépôt de la demande */
            <TimelineStep
                stepId="step-depot"
                title="2. Dépôt de la demande"
                delayLabel="Dépôt immédiat"
                completed={completedSteps.includes("step-depot")}
            >
                <p className="font-bold mb-2">
                    Vous déposez la demande (introductive d’instance ou conjointe) 
                    et payez les frais de greffe.
                </p>
                <p className="text-sm">
                    Une fois déposée, votre dossier est officiellement ouvert au tribunal.
                </p>
            </TimelineStep>

            {/* ÉTAPE 1 : Condition de résidence */}
            <TimelineStep
                stepId="step-residence"
                title="1. Condition de résidence"
                delayLabel="Aucun délai"
                completed={completedSteps.includes("step-residence")}
            >
                <p className="font-bold mb-2">
                    L’un des époux doit résider au Québec depuis au moins 12 mois.
                </p>
                <p className="text-sm">
                    Cela confirme la compétence de la Cour supérieure du Québec pour votre divorce.
                </p>
            </TimelineStep>
                                </div>
                            </div>

                            {/* BARRE VERTICALE */}
                            <div className="hidden lg:block bg-blue-200 w-[5px]" />

                            {/* COLONNE DE DROITE : Mini-sommaire */}
                            <div className="bg-gray-50 p-4">
                                <h3 className="text-blue-700 font-bold">Mini-sommaire</h3>
                                <p className="text-sm mb-3">
                                    Suivez vos choix et les délais estimés ci-dessous :
                                </p>

                                <div className="bg-gray-100 border border-gray-200 rounded p-3 mb-4">
                                    <p className="font-semibold text-sm mb-2">Historique des choix :</p>
                                    <ul className="list-disc list-inside text-sm space-y-1">
                                        {historyEntries.map((entry, idx) => (
                                            <li key={idx}>{entry}</li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="bg-gray-100 border border-gray-200 rounded p-3 text-sm">
                                    <p className="font-semibold mb-2">Points d’attention :</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Médiation obligatoire s’il y a des enfants mineurs.</li>
                                        <li>Ordonnances de protection en cas de violence.</li>
                                        <li>Informer la DPJ si un dossier est ouvert.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Pied de page ou zone libre */}
                        <footer className="bg-blue-50 text-center p-4 text-sm text-gray-500">
                            &copy; 2025 - Exemple myPlum+ / Simulation de procédure
                        </footer>
                    </div>
                );
            };

            // ------------------------------------------------------------------
            // Sous-composant <TimelineStep> : un bloc “accordéon” pour chaque étape
            // ------------------------------------------------------------------
            const TimelineStep = ({
                stepId,
                title,
                delayLabel,
                completed = false,
                defaultHidden = false,
                children,
            }) => {
                const [isOpen, setIsOpen] = React.useState(!defaultHidden);
                return (
                    <div
                        id={stepId}
                        className="my-2 bg-white shadow-sm transition-shadow hover:shadow-md"
                    >
                        {/* Header cliquable */}
                        <div
                            className="flex items-center justify-between bg-blue-50 p-2 cursor-pointer"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <h3 className="font-semibold text-blue-800 text-sm md:text-base mb-0">
                                {title}
                            </h3>
                            <div className="text-xs text-blue-600 ml-2 mr-2 bg-blue-100 p-1 rounded">
                                {delayLabel}
                            </div>
                            <span
                                className={`ml-auto mr-2 transform transition-transform ${
                                    isOpen ? "rotate-180" : ""
                                }`}
                            >
                                ▼
                            </span>
                        </div>
                        {/* Contenu */}
                        {isOpen && (
                            <div className="p-3 text-sm text-gray-700">
                                {children}
                            </div>
                        )}
                    </div>
                );
            };

            export default ProcedureSimulation;
